import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatTooltip } from '@angular/material/tooltip';
import { merge, Observable, of, Subject } from 'rxjs';
import { debounceTime, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TooltipOnTruncateDirective } from '../../../shared/directives/tooltip-on-truncate.directive';
import { ARCHIVE_FOLDER_NAME, SidebarFolderId } from '../../data-rules-model';

export interface IFolderOption {
  id: string;
  name: string;
  icon: string;
  colour?: string;
  outlinedIcon?: boolean;
}

export interface IFolderCreateItem {
  __create: true;
  name: string;
}

type FolderAutocompleteItem = IFolderOption | IFolderCreateItem;

@Component({
  selector: 'app-folder-autocomplete',
  standalone: true,
  templateUrl: './folder-autocomplete.component.html',
  styleUrls: ['./folder-autocomplete.component.scss'],
  imports: [
    NgIf,
    NgForOf,
    AsyncPipe,
    ReactiveFormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatTooltip,
    TooltipOnTruncateDirective
  ]
})
export class FolderAutocompleteComponent implements OnInit, OnChanges, OnDestroy {
  @Input() staticOptions: IFolderOption[] = [];
  @Input() loadOptions?: () => Observable<IFolderOption[]>;
  @Input() placeholder = 'Search folders...';
  @Input() showArchive = false;
  @Input() allowCreate = false;
  @Input() existingNames: string[] = [];
  @Input() reservedFolderNames: string[] = [];
  @Output() folderSelected = new EventEmitter<IFolderOption>();
  @Output() folderCreated = new EventEmitter<string>();
  @Output() closeRequested = new EventEmitter<void>();
  @Output() searchChanged = new EventEmitter<string>();

  @ViewChild(MatAutocompleteTrigger) private trigger: MatAutocompleteTrigger;

  readonly archiveOption: IFolderOption = { id: SidebarFolderId.ARCHIVE, name: ARCHIVE_FOLDER_NAME, icon: 'inventory_2', outlinedIcon: true };

  searchControl = new FormControl('');
  showArchiveOption$: Observable<boolean>;
  filteredOptions$: Observable<FolderAutocompleteItem[]>;
  loading = false;
  private destroy$ = new Subject<void>();
  private optionsRefresh$ = new Subject<string>();

  ngOnChanges(): void {
    this.optionsRefresh$.next(this.searchControl.value ?? '');
  }

  ngOnInit(): void {
    this.filteredOptions$ = merge(
      this.searchControl.valueChanges.pipe(debounceTime(300)),
      this.optionsRefresh$
    ).pipe(
      startWith(''),
      map(value => (typeof value === 'string' ? value : '')),
      switchMap(term => {
        const t = term.toLowerCase().trim();
        const folders$ = this.loadOptions
          ? (this.loading = true, this.loadOptions().pipe(tap(() => this.loading = false)))
          : of(this.staticOptions);
        return folders$.pipe(
          map(folders => this.filterFolders(folders, t)),
          map(folders => this.composeItems(term, folders))
        );
      }),
      takeUntil(this.destroy$)
    );

    this.showArchiveOption$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      map(value => {
        const term = (typeof value === 'string' ? value : '').toLowerCase().trim();
        return !term || 'archive'.includes(term);
      }),
      takeUntil(this.destroy$)
    );
  }

  clear(): void {
    this.searchControl.setValue('');
    this.searchChanged.emit('');
  }

  onInput(): void {
    this.searchChanged.emit(this.searchControl.value ?? '');
  }

  closePanel(): void {
    this.trigger?.closePanel();
  }

  displayFn(): string {
    return '';
  }

  isCreateItem(item: FolderAutocompleteItem): item is IFolderCreateItem {
    return (item as IFolderCreateItem).__create === true;
  }

  onEscape(event: Event): void {
    event.stopPropagation();
    if (this.searchControl.value) {
      this.clear();
    } else {
      this.closeRequested.emit();
    }
  }

  onSelected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.value as FolderAutocompleteItem;
    if (this.isCreateItem(value)) {
      this.folderCreated.emit(value.name);
    } else {
      this.folderSelected.emit(value);
    }
    this.searchControl.setValue('', { emitEvent: false });
  }

  private filterFolders(folders: IFolderOption[], term: string): IFolderOption[] {
    if (!term) {
      return folders;
    }
    return folders.filter(f => f.name.toLowerCase().includes(term));
  }

  private composeItems(term: string, folders: IFolderOption[]): FolderAutocompleteItem[] {
    const trimmed = (term ?? '').trim();
    if (!this.allowCreate || !trimmed) {
      return folders;
    }
    const lower = trimmed.toLowerCase();
    const namesToCheck = [
      ...(this.staticOptions ?? []).map(f => f.name),
      ...(this.existingNames ?? []),
      ...(this.reservedFolderNames ?? []),
      ...(this.showArchive ? [this.archiveOption.name] : [])
    ];
    const exactMatch = namesToCheck.some(n => n.trim().toLowerCase() === lower);
    if (exactMatch) {
      return folders;
    }
    return [...folders, { __create: true, name: trimmed }];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
